import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import { useAuthStore } from '../stores/auth';

let redirecting401 = false

const MAX_RETRIES = 2;
const RETRY_DELAY = 1500;
const recent429: Record<string, number> = {};
const MAX_GENERAL_RETRIES = 1;
const RETRY_STATUSES = new Set([502, 503, 504]);

export function normalizeBaseURL(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url, (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:7000'));
    u.hash = '';
    u.search = '';
    u.pathname = String(u.pathname || '').replace(/\/+$/, '');
    return u.toString().replace(/\/+$/, '');
  } catch {
    return String(url || '').replace(/\/+$/, '');
  }
}

function normalizeHostname(hostname: string): string {
  return String(hostname || '')
    .toLowerCase()
    .replace(/^\[|\]$/g, '')
    .replace(/\.$/, '')
}

function isPrivateHostname(hostname: string): boolean {
  const h = normalizeHostname(hostname)
  if (!h) return false
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return true
  if (/^10\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.test(h)) return true
  if (/^192\.168\.(\d{1,3})\.(\d{1,3})$/.test(h)) return true
  if (/^172\.(1[6-9]|2\d|3[0-1])\.(\d{1,3})\.(\d{1,3})$/.test(h)) return true
  if (/^(fc|fd)[0-9a-f]{0,2}:/i.test(h)) return true
  if (/^fe80:/i.test(h)) return true
  return false
}

export function normalizeResourceURL(inputUrl: string, baseUrl?: string): string {
  const raw = String(inputUrl || '').trim()
  if (!raw) return ''
  const base = normalizeBaseURL(
    String(
      baseUrl ||
        (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:7000')
    )
  )
  try {
    if (/^https?:\/\//i.test(raw)) {
      const u = new URL(raw)
      if (typeof window !== 'undefined') {
        const curHost = normalizeHostname(window.location.hostname || '')
        const uHost = normalizeHostname(u.hostname || '')
        if (!isPrivateHostname(curHost) && isPrivateHostname(uHost)) {
          const path = `${u.pathname}${u.search}${u.hash}`
          return new URL(path.startsWith('/') ? path : `/${path}`, base).toString()
        }
      }
      return u.toString()
    }
    return new URL(raw.startsWith('/') ? raw : `/${raw}`, base).toString()
  } catch {
    try {
      return new URL(raw, base).toString()
    } catch {
      return raw
    }
  }
}

export function computeApiBaseURL(): string {
  try {
    const envBase = ((typeof import.meta !== 'undefined' && (import.meta as unknown as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL) || '');
    const envNormalized = envBase ? normalizeBaseURL(envBase) : '';
    if (typeof window === 'undefined') {
      return envNormalized || 'http://localhost:7001'
    }

    const origin = window.location.origin
    const curHost = normalizeHostname(window.location.hostname || '')

    if (!envNormalized) {
      if (
        curHost === 'www.bandume.cn' ||
        curHost === 'bandume.cn' ||
        curHost === 'www.cnzip.cn' ||
        curHost === 'cnzip.cn'
      ) {
        const port = String(window.location.port || '')
        if (port === '7000') {
          return `${window.location.protocol}//${window.location.hostname}:7001`
        }
        return origin
      }
      return origin
    }
    try {
      const envUrl = new URL(envNormalized)
      const envHost = normalizeHostname(envUrl.hostname || '')
      if (!envHost) return origin
      if (envHost === curHost) return envNormalized
      if (isPrivateHostname(curHost)) return envNormalized
      if (isPrivateHostname(envHost)) {
        const derived = new URL(origin)
        derived.protocol = window.location.protocol
        derived.hostname = window.location.hostname
        derived.port = envUrl.port || '7001'
        const p = String(envUrl.pathname || '').replace(/\/+$/, '')
        derived.pathname = p && p !== '/' ? p : '/'
        derived.hash = ''
        derived.search = ''
        return derived.toString().replace(/\/+$/, '')
      }
      return envNormalized
    } catch {
      return origin
    }
  } catch {
    return 'http://localhost:7001';
  }
}

export function computePublicResourceBaseURL(): string {
  try {
    const envBase =
      ((typeof import.meta !== 'undefined' &&
        (import.meta as unknown as { env?: { VITE_RESOURCE_URL?: string; VITE_BOOKS_URL?: string } }).env?.VITE_RESOURCE_URL) ||
        (typeof import.meta !== 'undefined' &&
          (import.meta as unknown as { env?: { VITE_RESOURCE_URL?: string; VITE_BOOKS_URL?: string } }).env?.VITE_BOOKS_URL) ||
        '')
    const envNormalized = envBase ? normalizeBaseURL(envBase) : ''
    if (typeof window === 'undefined') {
      return envNormalized || computeApiBaseURL()
    }
    if (envNormalized) return envNormalized
    const host = normalizeHostname(window.location.hostname || '')
    if (host === 'www.bandume.cn' || host === 'bandume.cn' || host === 'www.cnzip.cn' || host === 'cnzip.cn') {
      const port = String(window.location.port || '')
      if (port === '7000') {
        return `${window.location.protocol}//${window.location.hostname}:7001`
      }
      return window.location.origin
    }
    return window.location.origin
  } catch {
    try {
      return typeof window !== 'undefined' ? window.location.origin : computeApiBaseURL()
    } catch {
      return computeApiBaseURL()
    }
  }
}

export function computeSameOriginResourceFallbackURL(inputUrl: string): string {
  try {
    if (typeof window === 'undefined') return String(inputUrl || '')
    const raw = String(inputUrl || '').trim()
    if (!raw) return ''
    const u = new URL(raw, window.location.origin)
    const path = `${u.pathname}${u.search}${u.hash}`
    if (!/^\/(books|previews)\//i.test(u.pathname)) return u.toString()
    if (u.origin === window.location.origin) return u.toString()
    return new URL(path.startsWith('/') ? path : `/${path}`, window.location.origin).toString()
  } catch {
    return String(inputUrl || '')
  }
}

const request = axios.create({
  baseURL: computeApiBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let authMeCache: { time: number; resp: AxiosResponse<unknown> } | null = null
let authMeInFlight: Promise<AxiosResponse<unknown>> | null = null

export function invalidateAuthMeCache(): void {
  authMeCache = null
}

export async function fetchAuthMeCached(options?: { skipRedirect?: boolean; force?: boolean }): Promise<AxiosResponse<unknown>> {
  const now = Date.now()
  const ttl = 10000
  if (!options?.force && authMeCache && now - authMeCache.time < ttl) {
    return authMeCache.resp
  }
  if (authMeInFlight) {
    return authMeInFlight
  }
  const headers: Record<string, string> = {}
  if (options?.skipRedirect) {
    headers['X-Skip-401-Redirect'] = '1'
  }
  const cfg: AxiosRequestConfig | undefined = Object.keys(headers).length > 0 ? { headers } : undefined
  authMeInFlight = request.get('/api/auth/me', cfg)
  try {
    const resp = await authMeInFlight
    authMeCache = { time: Date.now(), resp }
    return resp
  } finally {
    authMeInFlight = null
  }
}

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormData) {
      if (config.headers && (config.headers as Record<string, unknown>)['Content-Type']) {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
      }
      if (config.headers && (config.headers as Record<string, unknown>)['content-type']) {
        delete (config.headers as Record<string, unknown>)['content-type'];
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    // 处理成功的响应
    const { data } = response;
    const silent = !!(((response as AxiosResponse & { config?: AxiosRequestConfig })?.config?.headers as Record<string, unknown> | undefined)?.['X-Silent'] === '1')
    
    if (data.success === false) {
      const code = (data as { code?: unknown })?.code
      if (String(code || '') === 'TTS_DAILY_LIMIT_EXCEEDED') {
        if (!silent) {
          message.warning(data.message || '今日朗读额度已用完')
        }
        return Promise.reject(new Error(data.message || '今日朗读额度已用完'))
      }
      if (!silent) {
        message.error(data.message || '请求失败');
      }
      return Promise.reject(new Error(data.message || '请求失败'));
    }
    
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    const { config, response } = error;
    const cfg = config as AxiosRequestConfig & { __retryCount?: number };
    const method = String(cfg?.method || 'GET').toUpperCase();
    const headers = (cfg?.headers || {}) as Record<string, string>;
    const isFormDataReq = typeof FormData !== 'undefined' && (cfg?.data instanceof FormData);
    const noRetry = Boolean((headers['X-No-Retry'] === '1') || ((cfg as { noRetry?: boolean }).noRetry === true));
    const silent = !!(headers['X-Silent'] === '1');

    const errorCode = String((error as { code?: unknown })?.code || '')
    const errorMsg = String((error as { message?: unknown })?.message || '')
    const isTimeout = !!(error && (errorCode === 'ECONNABORTED' || /timeout/i.test(errorMsg)));
    const isConnReset = !!(error && ((errorCode === 'ECONNRESET') || /ERR_CONNECTION_RESET/i.test(errorMsg)));
    const isConnRefused = !!(error && (errorCode === 'ECONNREFUSED' || /ECONNREFUSED/i.test(errorMsg) || /ERR_CONNECTION_REFUSED/i.test(errorMsg)));
    const isNetworkError = !!(
      error &&
      !response &&
      (/network error/i.test(errorMsg) || errorCode === 'ERR_NETWORK' || isConnRefused)
    );
    const shouldRetryMethod = method === 'GET' || method === 'HEAD';

    if ((isTimeout || isConnReset || isNetworkError) && !noRetry) {
      if (shouldRetryMethod && !isFormDataReq) {
        cfg.__retryCount = (cfg.__retryCount || 0) + 1;
        if (cfg.__retryCount <= MAX_GENERAL_RETRIES) {
          const base = RETRY_DELAY * Math.pow(2, cfg.__retryCount - 1);
          const jitter = Math.floor(Math.random() * 500);
          const delay = base + jitter;
          try {
            if (!silent) {
              console.warn(`网络异常，${delay}ms后重试(${cfg.__retryCount}/${MAX_GENERAL_RETRIES})...`);
            }
          } catch { /* noop */ }
          await new Promise(resolve => setTimeout(resolve, delay));
          try {
            return await request(cfg);
          } catch (retryError) {
            if (!silent && isTimeout) {
              message.error('请求超时，请稍后重试');
            }
            return Promise.reject(retryError);
          }
        }
      }
      if (!silent && isTimeout) {
        message.error('请求超时，请稍后重试');
      }
      if (!silent && !isTimeout) {
        message.error('网络连接失败，请检查网络设置');
      }
      return Promise.reject(error);
    }
    
    const noRetry429 = !!(config && ((config.headers && (config.headers as Record<string, unknown>)['X-No-Retry'] === '1') || ((config as unknown as Record<string, unknown>)['noRetry'] === true)))
    if (response && response.status === 429 && config && !config.__retryCount && !noRetry429) {
      const key = String((config.url || '') + ':' + (config.method || 'GET'));
      const now = Date.now();
      const last = recent429[key] || 0;
      recent429[key] = now;
      if (now - last < 2000) {
        return Promise.reject(error);
      }
      config.__retryCount = 1;
      
      const base = RETRY_DELAY * Math.pow(2, config.__retryCount - 1);
      const jitter = Math.floor(Math.random() * 500);
      const delay = base + jitter;
      if (!silent) {
        console.warn(`请求过于频繁，${delay}ms后重试...`);
      }
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      try {
        return await request(config);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }
    
    if (response && response.status === 429 && config && config.__retryCount < MAX_RETRIES && !noRetry429) {
      const key = String((config.url || '') + ':' + (config.method || 'GET'));
      const now = Date.now();
      const last = recent429[key] || 0;
      recent429[key] = now;
      if (now - last < 2000) {
        return Promise.reject(error);
      }
      config.__retryCount += 1;
      
      const base = RETRY_DELAY * Math.pow(2, config.__retryCount - 1);
      const jitter = Math.floor(Math.random() * 500);
      const delay = base + jitter;
      if (!silent) {
        console.warn(`请求过于频繁，${delay}ms后重试(${config.__retryCount}/${MAX_RETRIES})...`);
      }
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      try {
        return await request(config);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }

  if (error.response) {
      const { status, data, config } = error.response
      const respCfg = config as AxiosRequestConfig & { __retryCount?: number }
      const skipRedirect = (respCfg?.headers && ((respCfg.headers as Record<string, string>)['X-Skip-401-Redirect'] === '1')) || ((respCfg as unknown as Record<string, unknown>)['skipAuthRedirect'] === true)
      const silentResp = !!((respCfg?.headers && ((respCfg.headers as Record<string, string>)['X-Silent'] === '1')))
      const role = (() => { try { return useAuthStore.getState().user?.role as string | undefined } catch { return undefined } })()
      const isAdminPath = typeof respCfg?.url === 'string' && /\/api\/admin\b/.test(String(respCfg.url))
      const methodResp = String(respCfg.method || 'GET').toUpperCase()
      const isFormDataResp = typeof FormData !== 'undefined' && (respCfg as AxiosRequestConfig)?.data instanceof FormData
      const noRetryResp = ((respCfg as unknown as Record<string, unknown>)['noRetry'] === true) || (((respCfg.headers || {}) as Record<string, string>)['X-No-Retry'] === '1')
      const canRetryResp = (RETRY_STATUSES.has(status)) && (methodResp === 'GET' || methodResp === 'HEAD') && !isFormDataResp && !noRetryResp
      if (canRetryResp && ((respCfg.__retryCount || 0) < MAX_GENERAL_RETRIES)) {
        respCfg.__retryCount = (respCfg.__retryCount || 0) + 1
        const base = RETRY_DELAY * Math.pow(2, respCfg.__retryCount - 1)
        const jitter = Math.floor(Math.random() * 500)
        const delay = base + jitter
        await new Promise(resolve => setTimeout(resolve, delay))
        try {
          return await request(respCfg)
        } catch (retryError) {
          if (!silentResp) {
            message.error('服务器暂时不可用，请稍后重试')
          }
          return Promise.reject(retryError)
        }
      }
      if (status === 401) {
        if (!skipRedirect) {
          try { useAuthStore.getState().logout() } catch { localStorage.removeItem('token') }
        }
        if (!skipRedirect && !redirecting401 && window.location.pathname !== '/login') {
          redirecting401 = true
          message.error('登录已过期，请重新登录')
          const from = (() => {
            try {
              const p = String(window.location.pathname || '/')
              const s = String(window.location.search || '')
              const h = String(window.location.hash || '')
              const cur = p + s + h
              if (!cur) return ''
              if (/^\/login\b/.test(p)) return ''
              return `?from=${encodeURIComponent(cur)}`
            } catch {
              return ''
            }
          })()
          setTimeout(() => { window.location.href = `/login${from}`; redirecting401 = false }, 0)
        }
        return Promise.reject(error)
      }
      if (status === 403) {
        if (!(silentResp || (isAdminPath && role !== 'admin' && role !== 'super_admin'))) {
          message.error('没有权限访问该资源')
        }
        return Promise.reject(error)
      }
      if (status === 404) {
        if (!silentResp) {
          message.error('请求的资源不存在')
        }
        return Promise.reject(error)
      }
      if (status === 429) {
        const code = (data as { code?: unknown })?.code
        const msg = String((data as { message?: unknown })?.message || '')
        const isQuotaLimit = String(code || '') === 'TTS_DAILY_LIMIT_EXCEEDED' || /额度已用完|朗读额度/i.test(msg)
        if (!silentResp && !isQuotaLimit) {
          console.warn('请求过于频繁，请稍后再试')
        }
        return Promise.reject(error)
      }
      if (status === 500) {
        const silent500 = !!(config && (config.headers && (config.headers as Record<string, unknown>)['X-Silent'] === '1'))
        if (!silent500) {
          message.error('服务器内部错误')
        }
        return Promise.reject(error)
      }
      if (!silentResp) {
        message.error(data?.message || '请求失败')
      }
      return Promise.reject(error)
    }
    if (error.request) {
      if (!silent) {
        message.error('网络连接失败，请检查网络设置')
      }
      return Promise.reject(error)
    }
    message.error('请求配置错误')
    return Promise.reject(error)
  }
);

export { request };
